import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { 
  FaArrowRight, FaChartLine, FaUsers, FaCloud, 
  FaRobot, FaShieldAlt, FaCheckCircle, FaQuoteLeft, 
  FaStar, FaPlay, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt,
  FaHeadset, FaDatabase, FaRocket
} from 'react-icons/fa';
import CountUp from 'react-countup';

const Home = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [statsInView] = useInView({ triggerOnce: true, threshold: 0.3 });

  const stats = [
    { icon: FaUsers, value: 5, suffix: '+', label: 'CLIENTS' },
    { icon: FaRocket, value: 15, suffix: '+', label: 'PROJECTS' },
    { icon: FaChartLine, value: 98, suffix: '%', label: 'SATISFACTION' },
    { icon: FaHeadset, value: 24, suffix: '/7', label: 'SUPPORT' },
  ];

  const capabilities = [
    {
      icon: FaCloud,
      title: 'Cloud CRM Solutions',
      description: 'Enterprise-grade cloud CRM with 99.9% uptime and global accessibility',
      path: '/solutions',
    },
    {
      icon: FaRobot,
      title: 'AI-Powered Automation',
      description: 'Intelligent process automation with machine learning for smarter workflows',
      path: '/solutions',
    },
    {
      icon: FaDatabase,
      title: 'Data Integration Hub',
      description: 'Seamless integration with 500+ apps including Salesforce and ERP systems',
      path: '/products',
    },
    {
      icon: FaShieldAlt,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and compliance',
      path: '/solutions',
    },
  ];

  const products = [
    {
      icon: FaUsers,
      title: 'SalesForce Optimizer',
      desc: 'AI-enhanced SalesForce implementation and optimization services',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    },
    {
      icon: FaChartLine,
      title: 'Analytics Dashboard',
      desc: 'Real-time business intelligence and predictive analytics platform',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    },
    {
      icon: FaHeadset,
      title: 'Customer 360°',
      desc: 'Unified customer view across all touchpoints and channels',
      image: 'https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    },
  ];

  // Generate random particles
  const particles = React.useMemo(() => {
    return [...Array(10)].map((_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2,
    }));
  }, []);

  return (
   <div className="min-h-screen">
  {/* Hero Section - Now using h-screen for exact viewport height */}
  <section className="relative h-screen flex flex-col justify-center overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/95 to-primary-950/80 z-10" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/50 z-10" />
      
      <motion.div
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0"
      >
        <img
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="CRM Technology"
          className="w-full h-full object-cover opacity-40"
        />
      </motion.div>

      <div className="absolute inset-0 bg-grid-pattern bg-grid z-20 opacity-[0.03]" />
    </div>

    <div className="absolute inset-0 z-20">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute w-1 h-1 bg-accent/40 rounded-full"
          style={{
            left: particle.left,
            top: particle.top,
          }}
          animate={{
            y: [0, -25, 0],
            opacity: [0, 0.8, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
          }}
        />
      ))}
    </div>

    {/* Main content - centered vertically with top padding for navbar spacing */}
    <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-22 md:pt-30 lg:pt-36" ref={heroRef}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={heroInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="max-w-4xl mx-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-5 md:mb-6"
        >
          <span className="px-3 py-1.5 md:px-4 md:py-2 bg-accent/10 border border-accent/30 text-accent 
                        font-display text-[10px] md:text-xs tracking-[0.3em] backdrop-blur-sm">
            NEXTGEN SOLUTION
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold leading-[1.1] sm:leading-[1.15] mb-4 md:mb-5 text-center"
        >
          <span className="text-white">TRANSFORM YOUR</span>
          <br />
          <span className="bg-gradient-to-r from-accent via-accent-light to-yellow-400 bg-clip-text text-transparent">
            CUSTOMER
          </span>
          {' '}
          <span className="text-white">RELATIONSHIPS</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="text-sm md:text-base lg:text-lg text-gray-300 max-w-xl mx-auto mb-5 md:mb-6 leading-relaxed text-center"
        >
          Enterprise CRM solutions, Salesforce optimization, and AI-powered automation 
          to help you build lasting customer relationships.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7 }}
          className="flex flex-wrap justify-center gap-3 mb-6 md:mb-8"
        >
          <Link to="/products">
            <span className="btn-primary group flex items-center space-x-2 cursor-pointer text-sm md:text-base">
              <span>EXPLORE SOLUTIONS</span>
              <FaArrowRight className="group-hover:translate-x-1 transition-transform text-xs" />
            </span>
          </Link>
          
          <Link to="/solutions">
            <span className="btn-outline group flex items-center space-x-2 cursor-pointer text-sm md:text-base">
              <FaPlay className="text-[10px]" />
              <span>OUR TECHNOLOGY</span>
            </span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3 max-w-3xl mx-auto mb-16 md:mb-20"
        >
          {stats.map((stat, index) => (
            <div
              key={index}
              className="glass-card p-2 md:p-3 text-center hover:-translate-y-1 transition-transform duration-300"
            >
              <stat.icon className="text-accent text-sm md:text-base mx-auto mb-1 md:mb-1.5" />
              <div className="text-base md:text-xl lg:text-2xl font-display font-bold text-white leading-none">
                {statsInView && (
                  <CountUp end={stat.value} duration={2} suffix={stat.suffix} />
                )}
              </div>
              <p className="text-[8px] md:text-[9px] lg:text-[10px] text-gray-500 mt-0.5 tracking-wider leading-tight">
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </div>

    {/* Left Scroll Indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.2, duration: 0.8 }}
      className="absolute bottom-6 md:bottom-8 left-4 sm:left-6 md:left-8 z-40"
    >
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="flex flex-col items-center space-y-1.5 cursor-pointer"
        onClick={() => {
          const nextSection = document.querySelector('#capabilities-section');
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          } else {
            window.scrollTo({
              top: window.innerHeight,
              behavior: 'smooth'
            });
          }
        }}
      >
        <span className="text-gray-500 font-display text-[8px] md:text-[9px] tracking-[0.3em]">SCROLL</span>
        <div className="w-3.5 h-5 md:w-4 md:h-6 border border-gray-600 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-0.5 h-1.5 bg-accent rounded-full mt-1.5"
          />
        </div>
      </motion.div>
    </motion.div>

    {/* Right Scroll Indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.2, duration: 0.8 }}
      className="absolute bottom-6 md:bottom-8 right-4 sm:right-6 md:right-8 z-40"
    >
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="flex flex-col items-center space-y-1.5 cursor-pointer"
        onClick={() => {
          const nextSection = document.querySelector('#capabilities-section');
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          } else {
            window.scrollTo({
              top: window.innerHeight,
              behavior: 'smooth'
            });
          }
        }}
      >
        <span className="text-gray-500 font-display text-[8px] md:text-[9px] tracking-[0.3em]">SCROLL</span>
        <div className="w-3.5 h-5 md:w-4 md:h-6 border border-gray-600 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-0.5 h-1.5 bg-accent rounded-full mt-1.5"
          />
        </div>
      </motion.div>
    </motion.div>
  </section>

  {/* Capabilities Section */}
  <section id="capabilities-section" className="relative py-16 md:py-20 bg-primary-900">
    <div className="absolute inset-0">
      <div className="absolute top-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center mb-10 md:mb-12"
      >
        <span className="text-accent font-display text-xs tracking-[0.3em]">WHAT WE DO</span>
        <h2 className="text-3xl md:text-5xl font-display font-bold text-white mt-3 mb-3">
          Core <span className="text-accent">Capabilities</span>
        </h2>
        <p className="text-gray-400 text-sm max-w-xl mx-auto">
          Comprehensive CRM and customer management solutions for modern enterprises
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
        {capabilities.map((cap, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <Link to={cap.path} className="block h-full">
              <div className="glass-card p-5 md:p-6 h-full group cursor-pointer relative overflow-hidden">
                <div className="relative z-10">
                  <div className="w-10 h-10 md:w-11 md:h-11 bg-accent/10 rounded-lg flex items-center justify-center 
                                mb-3 md:mb-4 group-hover:bg-accent/20 transition-all">
                    <cap.icon className="text-lg md:text-xl text-accent" />
                  </div>
                  <h3 className="text-sm md:text-base font-display font-bold text-white mb-2">
                    {cap.title}
                  </h3>
                  <p className="text-gray-400 text-xs leading-relaxed mb-3">
                    {cap.description}
                  </p>
                  <span className="flex items-center space-x-1 text-accent text-xs font-display tracking-wider">
                    <span>LEARN MORE</span>
                    <FaArrowRight className="text-[9px] group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>

  {/* About Preview */}
  <section className="relative py-16 md:py-20 bg-primary-950 overflow-hidden">
    <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.02]" />
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <span className="text-accent font-display text-xs tracking-[0.3em]">WHO WE ARE</span>
          <h2 className="text-2xl md:text-4xl font-display font-bold text-white mt-3 mb-4">
            <span className="text-accent">Sronzer Technologies</span> – Your CRM Partner
          </h2>
          <div className="golden-line mb-5 md:mb-6" />
          
          <p className="text-gray-300 text-sm leading-relaxed mb-4">
            We're a team of <span className="text-accent font-semibold">excellent developers and CRM experts</span> 
            dedicated to helping businesses build better customer relationships. Our developers are capable of 
            delivering anything our clients ask for.
          </p>
          
          <p className="text-gray-400 text-sm leading-relaxed mb-5 md:mb-6">
            From Salesforce implementation to custom CRM development, we provide end-to-end solutions 
            that transform how you manage customer interactions, sales pipelines, and support operations.
          </p>

          <div className="grid grid-cols-2 gap-2 mb-6">
            {[
              'Salesforce Platinum Partner',
              
              '24/7 Support Team',
              '10+ Successful Deployments',
            ].map((cert, i) => (
              <div key={i} className="flex items-center space-x-2">
                <FaCheckCircle className="text-accent text-xs flex-shrink-0" />
                <span className="text-gray-400 text-xs">{cert}</span>
              </div>
            ))}
          </div>

          <Link to="/about">
            <span className="btn-primary inline-flex cursor-pointer">
              ABOUT SRONZER
            </span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="grid grid-cols-2 gap-2 md:gap-3">
            <div className="space-y-2 md:space-y-3">
              <div className="glass-card overflow-hidden h-32 md:h-36">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Team Collaboration"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="glass-card overflow-hidden h-40 md:h-44">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZGFzaGJvYXJkfGVufDB8fDB8fHww"
                  alt="CRM Dashboard"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            </div>
            <div className="space-y-2 md:space-y-3 pt-5 md:pt-6">
              <div className="glass-card overflow-hidden h-40 md:h-44">
                <img 
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Salesforce"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="glass-card overflow-hidden h-32 md:h-36">
                <img 
                  src="https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Customer Support"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>

  {/* Products Preview */}
  <section className="relative py-16 md:py-20 bg-primary-900">
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center mb-10 md:mb-12"
      >
        <span className="text-accent font-display text-xs tracking-[0.3em]">OUR PRODUCTS</span>
        <h2 className="text-3xl md:text-5xl font-display font-bold text-white mt-3 mb-3">
          Featured <span className="text-accent">Solutions</span>
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4 md:gap-5 mb-8">
        {products.map((product, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <Link to="/products" className="block">
              <div className="glass-card overflow-hidden group cursor-pointer">
                <div className="h-36 md:h-40 overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <product.icon className="text-accent text-lg md:text-xl mb-2 md:mb-3" />
                  <h3 className="text-sm md:text-base font-display font-bold text-white mb-1.5">{product.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{product.desc}</p>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <Link 
          to="/products"
          className="inline-flex items-center space-x-2 text-accent hover:text-accent-light 
                   transition-colors font-display text-sm tracking-wider"
        >
          <span>VIEW ALL SOLUTIONS</span>
          <FaArrowRight className="text-xs" />
        </Link>
      </motion.div>
    </div>
  </section>

  {/* Testimonial */}
  <section className="relative py-14 md:py-16 bg-primary-950 overflow-hidden">
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <FaQuoteLeft className="text-3xl md:text-4xl text-accent/20 mx-auto mb-4 md:mb-6" />
        
        <p className="text-base md:text-xl text-gray-300 italic leading-relaxed mb-6">
          "Sronzer transformed our customer management completely. Their Salesforce expertise 
          and custom solutions helped us increase sales efficiency by 40%."
        </p>

        <div className="flex items-center justify-center space-x-1.5 mb-2">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} className="text-accent text-xs md:text-sm" />
          ))}
        </div>
        
        <p className="text-white font-display font-bold text-sm">Enterprise Tech Review</p>
        <p className="text-gray-500 text-xs">Customer Success Story, 2025</p>
      </motion.div>
    </div>
  </section>

  {/* CTA Section */}
  <section className="relative py-16 md:py-20 bg-primary-900 overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/50 to-transparent z-10" />
      <img 
        src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
        alt="CTA Background"
        className="w-full h-full object-cover opacity-20"
      />
    </div>

    <div className="relative z-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 className="text-2xl md:text-4xl font-display font-bold text-white mb-4">
          Ready to Transform Your <span className="text-accent">Customer Relationships</span>?
        </h2>
        <p className="text-gray-400 text-sm mb-6 md:mb-8 max-w-xl mx-auto">
          Our expert team is ready to help you implement the perfect CRM solution for your business
        </p>
        
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <Link to="/contact">
            <span className="btn-primary group flex items-center space-x-2 cursor-pointer">
              <FaPhoneAlt className="text-xs" />
              <span>CONTACT US</span>
            </span>
          </Link>
          
          <Link to="/products">
            <span className="btn-outline cursor-pointer inline-flex">
              EXPLORE SOLUTIONS
            </span>
          </Link>
        </div>

        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-gray-500">
          <a href="tel:+91 - 9277128460" className="flex items-center space-x-1.5 hover:text-accent transition-colors">
            <FaPhoneAlt className="text-accent text-[10px]" />
            <span>+91 - 9277128460</span>
          </a>
          <a href="mailto:connect@sronzer.com" className="flex items-center space-x-1.5 hover:text-accent transition-colors">
            <FaEnvelope className="text-accent text-[10px]" />
            <span>connect@sronzer.com</span>
          </a>
          <span className="flex items-center space-x-1.5">
            <FaMapMarkerAlt className="text-accent text-[10px]" />
            <span>Varanasi, India</span>
          </span>
        </div>
      </motion.div>
    </div>
  </section>
</div>
  );
};

export default Home;